import { DEFAULT_SEARCH_ISSUES_PERIOD } from "./constants-CEZaLsGe.js";
import { ConfigurationError } from "./errors-CEDWDXew.js";
import { isNumericId } from "./slug-validation-MJ3jU0Hd.js";
import { logIssue, logWarn } from "./logging-B7X3yL0T.js";
import { isMetricsDataset, isProfilesDataset, normalizeEventsDataset } from "./events-datasets-Cu37u43g.js";
import { getAIConversationUrl, getAIConversationsUrl, getContinuousProfileUrl, getDashboardUrl, getIssueUrl, getMonitorUrl, getPreprodSnapshotUrl, getProfileUrl, getProfilingExplorerUrl, getReleaseUrl, getReplayUrl, getReplaysSearchUrl, getTraceMetricsExploreUrl, getTraceUrl, getUptimeMonitorUrl, isSentryHost } from "./url-utils-CYEhmNHi.js";
import { USER_AGENT } from "./version-Dl3bNTAd.js";
import { apiPath } from "./api-path-BQDQ2sHi.js";
import { ApiNotFoundError, ApiValidationError, createApiError } from "./errors-DG2FreDU.js";
import { AIConversationDetailsResponseSchema, AIConversationSummaryListSchema, AgenticOnboardingRunSchema, ApiErrorSchema, AutofixRunSchema, AutofixRunStateSchema, ClientKeyListSchema, ClientKeySchema, CommitListSchema, DashboardListSchema, DashboardSchema, DeployListSchema, ErrorsSearchResponseSchema, EventAttachmentListSchema, EventSchema, ExternalIssueListSchema, FlamegraphSchema, IssueActivityListResponseSchema, IssueAlertRuleListSchema, IssueCommentListSchema, IssueCommentSchema, IssueListSchema, IssueSchema, IssueTagValuesSchema, MetricAlertRuleListSchema, MetricAlertRuleSchema, MonitorCheckInListSchema, MonitorListSchema, MonitorSchema, MonitorStatsSchema, OrganizationListSchema, OrganizationSchema, ProfileChunkResponseSchema, ProjectListSchema, ProjectRepositoryMappingSchema, ProjectSchema, ReleaseDetailsSchema, ReleaseListSchema, ReplayDetailsSchema, ReplayIdsByResourceSchema, ReplayListResponseSchema, ReplayRecordingSegmentsSchema, RepositoryListSchema, SpansSearchResponseSchema, StacktraceLinkSchema, TagListSchema, TeamListSchema, TeamSchema, TraceMetaSchema, TraceSchema, TransactionProfileSchema, UptimeCheckListSchema, UptimeMonitorListSchema, UptimeMonitorSchema, UserReportListSchema, UserSchema } from "./schema-BYZYkgA1.js";
import { z } from "zod";

//#region src/api-client/client.ts
const SENTRY_MCP_SEARCH_EVENTS_REFERRER = "api.mcp.search-events";
/**
* Mapping of common network error codes to user-friendly messages.
* These help users understand and resolve connection issues.
*/
const NETWORK_ERROR_MESSAGES = {
	EAI_AGAIN: "DNS temporarily unavailable. Check your internet connection.",
	ENOTFOUND: "Hostname not found. Verify the URL is correct.",
	ECONNREFUSED: "Connection refused. Ensure the service is accessible.",
	ETIMEDOUT: "Connection timed out. Check network connectivity.",
	ECONNRESET: "Connection reset. Try again in a moment."
};
function normalizeStatsPeriod(statsPeriod) {
	const normalized = statsPeriod?.trim();
	return normalized || void 0;
}
function formatWorkflowNameQuery(query) {
	const escapedQuery = query.replace(/\\/g, "\\\\").replace(/"/g, "\\\"");
	return `name:"*${escapedQuery}*"`;
}
function filterAttachedIssueAlertRules(rules) {
	return rules.filter((rule) => (rule.detectorIds ?? []).length > 0);
}
function parseStatsPeriod(statsPeriod) {
	const match = /^(\d+)([smhdw])$/.exec(statsPeriod);
	if (!match) throw new ApiValidationError("statsPeriod must use a supported relative time format, such as `24h`, `7d`, or `2w`.");
	return {
		amount: Number(match[1]),
		unit: match[2]
	};
}
function getNextCursor(linkHeader) {
	if (!linkHeader) return null;
	for (const link of linkHeader.split(",")) {
		if (!link.includes("rel=\"next\"") || !link.includes("results=\"true\"")) continue;
		const cursorMatch = link.match(/cursor="([^"]+)"/);
		if (cursorMatch?.[1]) return cursorMatch[1];
	}
	return null;
}
const TraceItemAttributeTypeSchema = z.enum([
	"string",
	"number",
	"boolean"
]);
const TraceItemAttributeSourceSchema = z.object({
	source_type: z.enum(["sentry", "user"]),
	is_transformed_alias: z.boolean().optional()
});
const ValidationErrorSchema = z.string().nullable();
const EventsValidationIssueSchema = z.object({
	valid: z.boolean(),
	error: ValidationErrorSchema
}).transform(({ valid, error }) => ({
	valid,
	...error ? { error } : {}
}));
const EventsNamedValidationIssueSchema = z.object({
	name: z.string(),
	valid: z.boolean(),
	error: ValidationErrorSchema
}).transform(({ name, valid, error }) => ({
	name,
	valid,
	...error ? { error } : {}
}));
const EventsAttributeValidationSchema = z.object({
	name: z.string(),
	valid: z.boolean(),
	attrType: TraceItemAttributeTypeSchema.nullable(),
	error: ValidationErrorSchema
}).transform(({ name, valid, attrType, error }) => ({
	name,
	valid,
	...attrType ? { type: attrType } : {},
	...error ? { error } : {}
}));
const EventsValidationIssueListSchema = z.array(EventsValidationIssueSchema);
const EventsNamedValidationIssueListSchema = z.array(EventsNamedValidationIssueSchema);
const EventsAttributeValidationListSchema = z.array(EventsAttributeValidationSchema);
const EventsQueryValidationSchema = z.object({
	valid: z.boolean(),
	error: ValidationErrorSchema,
	fields: EventsAttributeValidationListSchema
}).transform(({ valid, error, fields }) => ({
	valid,
	fields,
	...error ? { error } : {}
}));
const EventsValidationResponseSchema = z.object({
	valid: z.boolean(),
	projects: EventsValidationIssueListSchema,
	dataset: EventsNamedValidationIssueListSchema,
	environment: EventsValidationIssueListSchema,
	field: EventsAttributeValidationListSchema,
	query: EventsQueryValidationSchema,
	orderby: EventsAttributeValidationListSchema
}).transform((result) => result);
const TraceItemAttributeSchema = z.object({
	key: z.string(),
	name: z.string(),
	attributeType: TraceItemAttributeTypeSchema,
	attributeSource: TraceItemAttributeSourceSchema,
	secondaryAliases: z.array(z.string()).optional()
}).transform(({ key, name, attributeType, attributeSource, secondaryAliases }) => ({
	key,
	name,
	type: attributeType,
	attributeSource,
	...secondaryAliases ? { secondaryAliases } : {}
}));
const TraceItemAttributeListSchema = z.array(TraceItemAttributeSchema);
/**
* Sentry API client service for interacting with Sentry's REST API.
*
* This service provides a comprehensive interface to Sentry's API endpoints,
* handling authentication, error processing, multi-region support, and
* response validation through Zod schemas.
*
* Key Features:
* - Multi-region support for Sentry SaaS and self-hosted instances
* - Automatic schema validation with Zod
* - Enhanced error handling with LLM-friendly messages
* - URL generation for Sentry resources (issues, traces)
* - Bearer token authentication
* - Uses HTTPS by default, with opt-in HTTP for self-hosted stdio deployments
*
* @example Basic Usage
* ```typescript
* const apiService = new SentryApiService({
*   accessToken: "your-token",
*   host: "sentry.io"
* });
*
* const orgs = await apiService.listOrganizations();
* const issues = await apiService.listIssues({
*   organizationSlug: "my-org",
*   query: "is:unresolved"
* });
* ```
*
* @example Multi-Region Support
* ```typescript
* // Self-hosted instance with hostname
* const selfHosted = new SentryApiService({
*   accessToken: "token",
*   host: "sentry.company.com"
* });
*
* // Regional endpoint override
* const issues = await apiService.listIssues(
*   { organizationSlug: "org" },
*   { host: "eu.sentry.io" }
* );
* ```
*/
var SentryApiService = class {
	accessToken;
	clientId;
	clientName;
	clientFamily;
	utmSource;
	host;
	protocol;
	apiPrefix;
	/**
	* Creates a new Sentry API service instance.
	*
	* Uses HTTPS by default. Stdio may opt into HTTP for self-hosted deployments.
	*
	* @param config Configuration object
	* @param config.accessToken OAuth access token for authentication (optional for some endpoints)
	* @param config.host Sentry hostname (e.g. "sentry.io", "sentry.example.com")
	* @param config.clientId DCR-registered OAuth client ID
	* @param config.clientName DCR-registered OAuth client name
	* @param config.clientFamily Bucketed client family (e.g. "claude-code", "cursor")
	* @param config.utmSource Sanitized MCP request attribution source
	*/
	constructor({ accessToken = null, host = "sentry.io", protocol = "https", clientId = null, clientName = null, clientFamily = null, utmSource = null }) {
		this.accessToken = accessToken;
		this.clientId = clientId;
		this.clientName = clientName;
		this.clientFamily = clientFamily;
		this.utmSource = utmSource;
		this.host = host;
		this.protocol = protocol;
		this.apiPrefix = `${protocol}://${host}/api/0`;
	}
	/**
	* Updates the host for API requests.
	*
	* Used for multi-region support or switching between Sentry instances.
	* Preserves the configured URL scheme.
	*
	* @param host New hostname to use for API requests
	*/
	setHost(host) {
		this.host = host;
		this.apiPrefix = `${this.protocol}://${this.host}/api/0`;
	}
	/**
	* Checks if the current host is Sentry SaaS (sentry.io).
	*
	* Used to determine API endpoint availability and URL formats.
	* Self-hosted instances may not have all endpoints available.
	*
	* @returns True if using Sentry SaaS, false for self-hosted instances
	*/
	isSaas() {
		return isSentryHost(this.host);
	}
	/**
	* Validates and applies time parameters to a URLSearchParams object.
	*
	* Enforces mutual exclusivity between relative (statsPeriod) and
	* absolute (start/end) time ranges, and ensures start/end are paired.
	*
	* @param queryParams The URLSearchParams to modify
	* @param statsPeriod Relative time period (e.g., "24h", "7d")
	* @param start Absolute start time (ISO 8601)
	* @param end Absolute end time (ISO 8601)
	* @throws {ApiValidationError} If time parameters are invalid
	*/
	applyTimeParams(queryParams, statsPeriod, start, end) {
		if (statsPeriod && (start || end)) throw new ApiValidationError("Cannot use both statsPeriod and start/end parameters. Use either statsPeriod for relative time or start/end for absolute time.");
		if (start && !end || !start && end) throw new ApiValidationError("Both start and end parameters must be provided together for absolute time ranges.");
		if (statsPeriod) queryParams.set("statsPeriod", statsPeriod);
		else if (start && end) {
			queryParams.set("start", start);
			queryParams.set("end", end);
		}
	}
	applyStatsMixinTimeParams(queryParams, statsPeriod, start, end, resolutionSeconds) {
		if (statsPeriod && (start || end)) throw new ApiValidationError("Cannot use both statsPeriod and start/end parameters. Use either statsPeriod for relative time or start/end for absolute time.");
		if (start && !end || !start && end) throw new ApiValidationError("Both start and end parameters must be provided together for absolute time ranges.");
		let since;
		let until;
		if (start && end) {
			const startMs = Date.parse(start);
			const endMs = Date.parse(end);
			if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) throw new ApiValidationError("start and end parameters must be valid ISO 8601 date strings.");
			since = Math.floor(startMs / 1e3);
			until = Math.floor(endMs / 1e3);
		} else {
			const period = statsPeriod ?? "24h";
			const { amount, unit } = parseStatsPeriod(period);
			let unitSeconds;
			switch (unit) {
				case "s":
					unitSeconds = 1;
					break;
				case "m":
					unitSeconds = 60;
					break;
				case "h":
					unitSeconds = 60 * 60;
					break;
				case "d":
					unitSeconds = 60 * 60 * 24;
					break;
				case "w":
					unitSeconds = 60 * 60 * 24 * 7;
					break;
				default: throw new ApiValidationError("statsPeriod must use a supported relative time format, such as `24h`, `7d`, or `2w`.");
			}
			until = Math.floor(Date.now() / 1e3);
			since = until - amount * unitSeconds;
		}
		queryParams.set("since", String(since));
		queryParams.set("until", String(until));
		if (resolutionSeconds !== void 0) queryParams.set("resolution", `${resolutionSeconds}s`);
	}
	/**
	* Internal method for making authenticated requests to Sentry API.
	*
	* Handles:
	* - Bearer token authentication
	* - Error response parsing and enhancement
	* - Multi-region host overrides
	* - Fetch availability validation
	*
	* @param path API endpoint path (without /api/0 prefix)
	* @param options Fetch options
	* @param requestOptions Additional request configuration
	* @returns Promise resolving to Response object
	* @throws {ApiError} Enhanced API errors with user-friendly messages
	* @throws {Error} Network or parsing errors
	*/
	async request(path, options = {}, { host, allowStatuses } = {}) {
		const url = host ? `${this.protocol}://${host}/api/0${path}` : `${this.apiPrefix}${path}`;
		const headers = {
			"Content-Type": "application/json",
			"User-Agent": USER_AGENT
		};
		if (this.accessToken) headers.Authorization = `Bearer ${this.accessToken}`;
		if (this.clientId) headers["X-Sentry-MCP-Client-Id"] = this.clientId;
		if (this.clientName) headers["X-Sentry-MCP-Client-Name"] = this.clientName;
		if (this.clientFamily) headers["X-Sentry-MCP-Client-Family"] = this.clientFamily;
		if (this.utmSource) headers["X-Sentry-MCP-Utm-Source"] = this.utmSource;
		if (typeof globalThis.fetch === "undefined") throw new ConfigurationError("fetch is not available. Please use Node.js >= 18 or ensure fetch is available in your environment.");
		let response;
		try {
			response = await fetch(url, {
				...options,
				headers
			});
		} catch (error) {
			let rootCause = error;
			while (rootCause instanceof Error && rootCause.cause) rootCause = rootCause.cause;
			const errorMessage = rootCause instanceof Error ? rootCause.message : String(rootCause);
			let friendlyMessage = `Unable to connect to ${url}`;
			const errorCode = Object.keys(NETWORK_ERROR_MESSAGES).find((code) => errorMessage.includes(code));
			if (errorCode) friendlyMessage += ` - ${NETWORK_ERROR_MESSAGES[errorCode]}`;
			else friendlyMessage += ` - ${errorMessage}`;
			if (errorCode === "ENOTFOUND" || errorCode === "EAI_AGAIN" || errorCode === "ECONNREFUSED" || errorCode === "ETIMEDOUT" || errorMessage.includes("Connect Timeout Error")) throw new ConfigurationError(friendlyMessage, { cause: error });
			throw new Error(friendlyMessage, { cause: error });
		}
		if (!response.ok) {
			if (allowStatuses?.includes(response.status)) return response;
			const errorText = await response.text();
			let parsed;
			try {
				parsed = JSON.parse(errorText);
			} catch (error) {
				if (errorText.includes("<!DOCTYPE") || errorText.includes("<html")) {
					logWarn("Received HTML error page instead of JSON", {
						loggerScope: ["api", "client"],
						extra: {
							status: response.status,
							statusText: response.statusText,
							host: this.host,
							path,
							parseErrorMessage: error instanceof Error ? error.message : String(error)
						}
					});
					throw createApiError(`Server error: Received HTML instead of JSON (${response.status} ${response.statusText}). This may indicate an invalid URL or server issue.`, response.status, errorText, void 0);
				}
				logWarn("Failed to parse JSON error response", {
					loggerScope: ["api", "client"],
					extra: {
						status: response.status,
						statusText: response.statusText,
						host: this.host,
						path,
						bodyPreview: errorText.length > 256 ? `${errorText.slice(0, 253)}…` : errorText,
						parseErrorMessage: error instanceof Error ? error.message : String(error)
					}
				});
			}
			if (parsed) {
				const { data, success, error } = ApiErrorSchema.safeParse(parsed);
				if (success) throw createApiError(data.detail, response.status, data.detail, parsed);
				logWarn("Failed to parse validated API error response", {
					loggerScope: ["api", "client"],
					extra: {
						status: response.status,
						statusText: response.statusText,
						host: this.host,
						path,
						bodyPreview: errorText.length > 256 ? `${errorText.slice(0, 253)}…` : errorText,
						validationErrorMessage: error instanceof Error ? error.message : String(error)
					}
				});
			}
			throw createApiError(`API request failed: ${response.statusText}\n${errorText}`, response.status, errorText, void 0);
		}
		return response;
	}
	/**
	* Safely parses a JSON response, checking Content-Type header first.
	*
	* @param response The Response object from fetch
	* @returns Promise resolving to the parsed JSON object
	* @throws {Error} If response is not JSON or parsing fails
	*/
	async parseJsonResponse(response) {
		if (!response.headers?.get) return response.json();
		const contentType = response.headers.get("content-type");
		if (!contentType || !contentType.includes("application/json")) {
			const responseText = await response.text();
			if (contentType?.includes("text/html") || responseText.includes("<!DOCTYPE") || responseText.includes("<html")) throw new Error(`Expected JSON response but received HTML (${response.status} ${response.statusText}). This may indicate you're not authenticated, the URL is incorrect, or there's a server issue.`);
			throw new Error(`Expected JSON response but received ${contentType || "unknown content type"} (${response.status} ${response.statusText})`);
		}
		try {
			return await response.json();
		} catch (error) {
			throw new Error(`Failed to parse JSON response: ${error instanceof Error ? error.message : String(error)}`);
		}
	}
	/**
	* Makes a request to the Sentry API and parses the JSON response.
	*
	* This is the primary method for API calls that expect JSON responses.
	* It automatically validates Content-Type and provides helpful error messages
	* for common issues like authentication failures or server errors.
	*
	* @param path API endpoint path (without /api/0 prefix)
	* @param options Fetch options
	* @param requestOptions Additional request configuration
	* @returns Promise resolving to the parsed JSON response
	* @throws {ApiError} Enhanced API errors with user-friendly messages
	* @throws {Error} Network, parsing, or validation errors
	*/
	async requestJSON(path, options = {}, requestOptions) {
		const response = await this.request(path, options, requestOptions);
		return this.parseJsonResponse(response);
	}
	/**
	* Generates a Sentry issue URL for browser navigation.
	*
	* Handles both SaaS (subdomain-based) and self-hosted URL formats.
	* Uses the configured protocol.
	*
	* @param organizationSlug Organization identifier
	* @param issueId Issue identifier (short ID or numeric ID)
	* @returns Full URL to the issue in Sentry UI
	*
	* @example
	* ```typescript
	* // SaaS: https://my-org.sentry.io/issues/PROJ-123
	* apiService.getIssueUrl("my-org", "PROJ-123")
	*
	* // Self-hosted: https://sentry.company.com/organizations/my-org/issues/PROJ-123
	* apiService.getIssueUrl("my-org", "PROJ-123")
	* ```
	*/
	getIssueUrl(organizationSlug, issueId) {
		return getIssueUrl(this.host, organizationSlug, issueId, this.protocol);
	}
	/**
	* Generates a Sentry trace URL for performance investigation.
	*
	* Uses the configured protocol.
	*
	* @param organizationSlug Organization identifier
	* @param traceId Trace identifier (hex string)
	* @returns Full URL to the trace in Sentry UI
	*
	* @example
	* ```typescript
	* const traceUrl = apiService.getTraceUrl("my-org", "6a477f5b0f31ef7b6b9b5e1dea66c91d");
	* // https://my-org.sentry.io/explore/traces/trace/6a477f5b0f31ef7b6b9b5e1dea66c91d
	* ```
	*/
	getTraceUrl(organizationSlug, traceId) {
		return getTraceUrl(this.host, organizationSlug, traceId, this.protocol);
	}
	getReplayUrl(organizationSlug, replayId) {
		return getReplayUrl(this.host, organizationSlug, replayId, this.protocol);
	}
	getAIConversationUrl(organizationSlug, conversationId) {
		return getAIConversationUrl(this.host, organizationSlug, conversationId, this.protocol);
	}
	/** Builds the Sentry UI URL for the AI Conversations list with optional filters. */
	getAIConversationsUrl(organizationSlug, options) {
		return getAIConversationsUrl(this.host, organizationSlug, options, this.protocol);
	}
	getProfileUrl(organizationSlug, projectSlug, profileId) {
		return getProfileUrl(this.host, organizationSlug, projectSlug, profileId, this.protocol);
	}
	getContinuousProfileUrl(organizationSlug, projectSlug, options) {
		return getContinuousProfileUrl(this.host, organizationSlug, projectSlug, options, this.protocol);
	}
	getReplaysSearchUrl(organizationSlug, options = {}) {
		return getReplaysSearchUrl(this.host, organizationSlug, options, this.protocol);
	}
	getMonitorUrl(organizationSlug, monitorSlug, projectSlug) {
		return getMonitorUrl(this.host, organizationSlug, monitorSlug, this.protocol, projectSlug);
	}
	getUptimeMonitorUrl(organizationSlug, uptimeMonitorId) {
		return getUptimeMonitorUrl(this.host, organizationSlug, uptimeMonitorId, this.protocol);
	}
	getReleaseUrl(organizationSlug, releaseVersion) {
		return getReleaseUrl(this.host, organizationSlug, releaseVersion, this.protocol);
	}
	getDashboardUrl(organizationSlug, dashboardId, options = {}) {
		return getDashboardUrl(this.host, organizationSlug, dashboardId, options, this.protocol);
	}
	getIssueAlertRuleUrl(organizationSlug, ruleId) {
		const encodedRuleId = encodeURIComponent(String(ruleId));
		if (this.isSaas()) return `${this.protocol}://${organizationSlug}.sentry.io/monitors/alerts/${encodedRuleId}/`;
		return `${this.protocol}://${this.host}/organizations/${organizationSlug}/monitors/alerts/${encodedRuleId}/`;
	}
	getMetricAlertRuleUrl(organizationSlug, ruleId) {
		const encodedRuleId = encodeURIComponent(String(ruleId));
		if (this.isSaas()) return `${this.protocol}://${organizationSlug}.sentry.io/issues/alerts/rules/details/${encodedRuleId}/`;
		return `${this.protocol}://${this.host}/organizations/${organizationSlug}/issues/alerts/rules/details/${encodedRuleId}/`;
	}
	/**
	* Builds a URL for the legacy Discover API (used by errors dataset).
	*
	* The Discover API is the older query interface that includes aggregate
	* functions directly in the field list.
	*
	* @example
	* // URL format: /explore/discover/homepage/?field=title&field=count_unique(user)
	* buildDiscoverUrl("my-org", "level:error", "123", ["title", "count_unique(user)"], "-timestamp")
	*/
	buildDiscoverUrl(params) {
		const { organizationSlug, query, projectId, fields, sort, statsPeriod, start, end, aggregateFunctions, groupByFields } = params;
		const urlParams = new URLSearchParams();
		urlParams.set("dataset", "errors");
		urlParams.set("queryDataset", "error-events");
		urlParams.set("query", query);
		if (projectId) urlParams.set("project", projectId);
		if (fields && fields.length > 0) for (const field of fields) urlParams.append("field", field);
		else {
			urlParams.append("field", "title");
			urlParams.append("field", "project");
			urlParams.append("field", "user.display");
			urlParams.append("field", "timestamp");
		}
		urlParams.set("sort", sort || "-timestamp");
		if (start && end) {
			urlParams.set("start", start);
			urlParams.set("end", end);
		} else urlParams.set("statsPeriod", statsPeriod || "24h");
		const isAggregate = (aggregateFunctions?.length ?? 0) > 0;
		if (isAggregate) {
			urlParams.set("mode", "aggregate");
			if (aggregateFunctions && aggregateFunctions.length > 0) urlParams.set("yAxis", aggregateFunctions[0]);
		} else urlParams.set("yAxis", "count()");
		const webHost = this.isSaas() ? "sentry.io" : this.host;
		const path = this.isSaas() ? `${this.protocol}://${organizationSlug}.${webHost}/explore/discover/homepage/` : `${this.protocol}://${this.host}/organizations/${organizationSlug}/explore/discover/homepage/`;
		return `${path}?${urlParams.toString()}`;
	}
	isAggregateExplorerQuery(params) {
		return Boolean(params.aggregateFunctions?.length || params.fields?.some((field) => field.includes("(") && field.includes(")")));
	}
	/**
	* Adds the structured aggregate fields shared by the Spans and Logs Explorer
	* URL formats. Each group-by and collection of y-axes is encoded as a
	* separate JSON-valued `aggregateField` parameter.
	*
	* @example
	* aggregateField={"groupBy":"span.op"}&aggregateField={"yAxes":["count()"]}
	*/
	appendAggregateFields(urlParams, params) {
		const { fields, aggregateFunctions, groupByFields } = params;
		if (aggregateFunctions?.length || groupByFields?.length) {
			for (const field of groupByFields ?? []) urlParams.append("aggregateField", JSON.stringify({ groupBy: field }));
			if (aggregateFunctions?.length) urlParams.append("aggregateField", JSON.stringify({ yAxes: aggregateFunctions }));
			return;
		}
		const parsedGroupByFields = fields?.filter((field) => !field.includes("(") && !field.includes(")")) ?? [];
		const parsedAggregateFunctions = fields?.filter((field) => field.includes("(") && field.includes(")")) ?? [];
		for (const field of parsedGroupByFields) urlParams.append("aggregateField", JSON.stringify({ groupBy: field }));
		if (parsedAggregateFunctions.length > 0) urlParams.append("aggregateField", JSON.stringify({ yAxes: parsedAggregateFunctions }));
	}
	appendExplorerTimeParams(urlParams, params) {
		if (params.start && params.end) {
			urlParams.set("start", params.start);
			urlParams.set("end", params.end);
		} else urlParams.set("statsPeriod", params.statsPeriod || "24h");
	}
	buildExplorePath(organizationSlug, page) {
		if (this.isSaas()) return `${this.protocol}://${organizationSlug}.sentry.io/explore/${page}/`;
		return `${this.protocol}://${this.host}/organizations/${organizationSlug}/explore/${page}/`;
	}
	/** Builds a Spans Explorer URL using its `query`, `field`, and `sort` contract. */
	buildSpansExplorerUrl(params) {
		const { organizationSlug, query, projectId, fields, sort } = params;
		const urlParams = new URLSearchParams();
		urlParams.set("query", query);
		if (projectId) urlParams.set("project", projectId);
		const isAggregateQuery = this.isAggregateExplorerQuery(params);
		if (isAggregateQuery) {
			this.appendAggregateFields(urlParams, params);
			urlParams.set("mode", "aggregate");
		} else for (const field of fields ?? []) urlParams.append("field", field);
		if (sort) urlParams.set("sort", sort);
		this.appendExplorerTimeParams(urlParams, params);
		urlParams.set("table", "span");
		return `${this.buildExplorePath(organizationSlug, "traces")}?${urlParams.toString()}`;
	}
	/**
	* Builds a Logs Explorer URL using its logs-specific page parameters.
	* Sample and aggregate views use distinct field and sort parameters, and an
	* omitted project is represented by `project=-1` to preserve all-project scope.
	*/
	buildLogsExplorerUrl(params) {
		const { organizationSlug, query, projectId, fields, sort } = params;
		const urlParams = new URLSearchParams();
		urlParams.set("logsQuery", query);
		urlParams.set("project", projectId ?? "-1");
		const isAggregateQuery = this.isAggregateExplorerQuery(params);
		if (isAggregateQuery) this.appendAggregateFields(urlParams, params);
		else for (const field of fields ?? []) urlParams.append("logsFields", field);
		urlParams.set("mode", isAggregateQuery ? "aggregate" : "samples");
		if (sort) urlParams.set(isAggregateQuery ? "logsAggregateSortBys" : "logsSortBys", sort);
		this.appendExplorerTimeParams(urlParams, params);
		return `${this.buildExplorePath(organizationSlug, "logs")}?${urlParams.toString()}`;
	}
	extractTraceMetricsFromResults(eventData) {
		const metrics = /* @__PURE__ */ new Map();
		for (const event of eventData ?? []) {
			const name = event["metric.name"];
			const type = event["metric.type"];
			const rawUnit = event["metric.unit"];
			if (typeof name !== "string" || typeof type !== "string") continue;
			const unit = typeof rawUnit === "string" && rawUnit !== "-" ? rawUnit : void 0;
			const key = `${name}|${type}|${unit ?? ""}`;
			if (!metrics.has(key)) metrics.set(key, {
				name,
				type,
				unit
			});
		}
		return [...metrics.values()];
	}
	/**
	* Generates a Sentry events explorer URL for viewing search results.
	*
	* Routes to the appropriate API based on dataset:
	* - Errors: Uses legacy Discover API
	* - Spans/Logs: Uses modern EAP (Event Analytics Platform) API
	* - Metrics: Uses the Metrics page URL format
	*
	* @param organizationSlug Organization identifier
	* @param query Sentry search query
	* @param projectId Optional project filter
	* @param dataset Dataset type (spans, errors, logs, or metrics)
	* @param fields Array of fields to include in results
	* @param sort Sort parameter (e.g., "-timestamp", "-count()")
	* @param aggregateFunctions Array of aggregate functions for aggregate queries
	* @param groupByFields Array of fields to group by for aggregate queries
	* @param statsPeriod Relative time period (e.g., "24h", "7d")
	* @param start Absolute start time (ISO 8601)
	* @param end Absolute end time (ISO 8601)
	* @param eventData Optional event rows used to derive trace metric identity for metrics sample URLs
	* @returns Full URL to the events explorer in Sentry UI
	*/
	getEventsExplorerUrl(organizationSlug, query, projectId, dataset = "spans", fields, sort, aggregateFunctions, groupByFields, statsPeriod, start, end, eventData) {
		if (dataset === "errors") return this.buildDiscoverUrl({
			organizationSlug,
			query,
			projectId,
			fields,
			sort,
			statsPeriod,
			start,
			end,
			aggregateFunctions,
			groupByFields
		});
		if (isMetricsDataset(dataset)) return getTraceMetricsExploreUrl(this.host, organizationSlug, {
			query,
			projectId,
			sort,
			statsPeriod,
			start,
			end,
			aggregateFunctions,
			groupByFields,
			traceMetrics: this.extractTraceMetricsFromResults(eventData)
		}, this.protocol);
		if (isProfilesDataset(dataset)) return getProfilingExplorerUrl(this.host, organizationSlug, {
			query,
			projectId,
			fields,
			sort,
			statsPeriod,
			start,
			end,
			aggregateFunctions,
			groupByFields
		}, this.protocol);
		const explorerParams = {
			organizationSlug,
			query,
			projectId,
			fields,
			sort,
			statsPeriod,
			start,
			end,
			aggregateFunctions,
			groupByFields
		};
		return dataset === "logs" ? this.buildLogsExplorerUrl(explorerParams) : this.buildSpansExplorerUrl(explorerParams);
	}
	/**
	* Retrieves the authenticated user's profile information.
	*
	* @param opts Request options including host override
	* @returns User profile data
	* @throws {ApiError} If authentication fails or user not found
	*/
	async getAuthenticatedUser(opts) {
		let authHost;
		if (this.isSaas()) authHost = "sentry.io";
		const body = await this.requestJSON("/auth/", void 0, {
			...opts,
			host: authHost
		});
		return UserSchema.parse(body);
	}
	/**
	* Lists all organizations accessible to the authenticated user.
	*
	* Queries the `/organizations/` endpoint on the root host.
	*
	* @param params Query parameters
	* @param params.query Search query to filter organizations by name/slug
	* @param params.limit Maximum number of organizations to return (defaults to 25)
	* @returns Array of organizations across all accessible regions
	*
	* @example
	* ```typescript
	* const orgs = await apiService.listOrganizations();
	* orgs.forEach(org => {
	*   // regionUrl present for Cloud Service, empty for self-hosted
	*   console.log(`${org.name} (${org.slug}) - ${org.links?.regionUrl || 'No region URL'}`);
	* });
	* ```
	*/
	async listOrganizations(params) {
		const limit = params?.limit ?? 25;
		const queryParams = new URLSearchParams();
		queryParams.set("per_page", String(limit));
		if (params?.query) queryParams.set("query", params.query);
		const queryString = queryParams.toString();
		const path = `/organizations/?${queryString}`;
		let host = void 0;
		if (this.isSaas()) host = "sentry.io";
		const body = await this.requestJSON(path, void 0, { host });
		return OrganizationListSchema.parse(body);
	}
	/**
	* Gets a single organization by slug.
	*
	* @param organizationSlug Organization identifier
	* @param opts Request options including host override
	* @returns Organization data
	*/
	async getOrganization(organizationSlug, opts) {
		const body = await this.requestJSON(apiPath`/organizations/${organizationSlug}/`, void 0, opts);
		return OrganizationSchema.parse(body);
	}
	/**
	* Lists teams within an organization.
	*
	* @param organizationSlug Organization identifier
	* @param params Query parameters
	* @param params.query Search query to filter teams by name/slug
	* @param params.limit Maximum number of teams to return
	* @param opts Request options including host override
	* @returns Array of teams in the organization
	*/
	async listTeams(organizationSlug, params, opts) {
		const queryParams = new URLSearchParams();
		queryParams.set("per_page", String(params?.limit ?? 25));
		if (params?.query) queryParams.set("query", params.query);
		const queryString = queryParams.toString();
		const teamsPath = apiPath`/organizations/${organizationSlug}/teams/`;
		const path = `${teamsPath}?${queryString}`;
		const body = await this.requestJSON(path, void 0, opts);
		return TeamListSchema.parse(body);
	}
	/**
	* Creates a new team within an organization.
	*
	* @param params Team creation parameters
	* @param params.organizationSlug Organization identifier
	* @param params.name Team name
	* @param opts Request options
	* @returns Created team data
	* @throws {ApiError} If team creation fails (e.g., name conflicts)
	*/
	async createTeam({ organizationSlug, name }, opts) {
		const body = await this.requestJSON(apiPath`/organizations/${organizationSlug}/teams/`, {
			method: "POST",
			body: JSON.stringify({ name })
		}, opts);
		return TeamSchema.parse(body);
	}
	/**
	* Lists projects within an organization.
	*
	* @param organizationSlug Organization identifier
	* @param params Query parameters
	* @param params.query Search query to filter projects by name/slug
	* @param params.limit Maximum number of projects to return
	* @param opts Request options
	* @returns Array of projects in the organization
	*/
	async listProjects(organizationSlug, params, opts) {
		const queryParams = new URLSearchParams();
		queryParams.set("per_page", String(params?.limit ?? 25));
		if (params?.query) queryParams.set("query", params.query);
		const queryString = queryParams.toString();
		const projectsPath = apiPath`/organizations/${organizationSlug}/projects/`;
		const path = `${projectsPath}?${queryString}`;
		const body = await this.requestJSON(path, void 0, opts);
		return ProjectListSchema.parse(body);
	}
	async listDashboards({ organizationSlug, query, sortBy, limit = 10, cursor }, opts) {
		const queryParams = new URLSearchParams();
		queryParams.set("per_page", String(limit));
		if (query) queryParams.set("query", query);
		if (sortBy) queryParams.set("sort", sortBy);
		if (cursor) queryParams.set("cursor", cursor);
		const response = await this.request(apiPath`/organizations/${organizationSlug}/dashboards/` + `?${queryParams.toString()}`, void 0, opts);
		const body = await this.parseJsonResponse(response);
		return {
			dashboards: DashboardListSchema.parse(body),
			nextCursor: getNextCursor(response.headers.get("link"))
		};
	}
	async getDashboard({ organizationSlug, dashboardId }, opts) {
		const body = await this.requestJSON(apiPath`/organizations/${organizationSlug}/dashboards/${dashboardId}/`, void 0, opts);
		return DashboardSchema.parse(body);
	}
	/**
	* Gets a single project by slug or ID.
	*
	* @param params Project fetch parameters
	* @param params.organizationSlug Organization identifier
	* @param params.projectSlugOrId Project slug or numeric ID
	* @param opts Request options
	* @returns Project data
	*/
	async getProject({ organizationSlug, projectSlugOrId }, opts) {
		const body = await this.requestJSON(apiPath`/projects/${organizationSlug}/${projectSlugOrId}/`, void 0, opts);
		return ProjectSchema.parse(body);
	}
	/**
	* Creates a new project within a team.
	*
	* @param params Project creation parameters
	* @param params.organizationSlug Organization identifier
	* @param params.teamSlug Team identifier
	* @param params.name Project name
	* @param params.slug Optional project slug
	* @param params.platform Platform identifier (e.g., "javascript", "python")
	* @param opts Request options
	* @returns Created project data
	*/
	async createProject({ organizationSlug, teamSlug, name, slug, platform }, opts) {
		const createData = { name };
		if (slug) createData.slug = slug;
		if (platform) createData.platform = platform;
		const body = await this.requestJSON(apiPath`/teams/${organizationSlug}/${teamSlug}/projects/`, {
			method: "POST",
			body: JSON.stringify(createData)
		}, opts);
		return ProjectSchema.parse(body);
	}
	/**
	* Updates an existing project's configuration.
	*
	* @param params Project update parameters
	* @param params.organizationSlug Organization identifier
	* @param params.projectSlug Current project identifier
	* @param params.name New project name (optional)
	* @param params.slug New project slug (optional)
	* @param params.platform New platform identifier (optional)
	* @param opts Request options
	* @returns Updated project data
	*/
	async updateAgenticOnboardingStatus({ organizationSlug, update }, opts) {
		const body = await this.requestJSON(apiPath`/organizations/${organizationSlug}/onboarding/agent/status/`, {
			method: "POST",
			body: JSON.stringify(update)
		}, opts);
		return AgenticOnboardingRunSchema.parse(body);
	}
	async updateProject({ organizationSlug, projectSlug, name, slug, platform }, opts) {
		const updateData = {};
		if (name) updateData.name = name;
		if (slug) updateData.slug = slug;
		if (platform) updateData.platform = platform;
		const body = await this.requestJSON(apiPath`/projects/${organizationSlug}/${projectSlug}/`, {
			method: "PUT",
			body: JSON.stringify(updateData)
		}, opts);
		return ProjectSchema.parse(body);
	}
	async listRepos({ organizationSlug, query }, opts) {
		let cursor = null;
		const repos = [];
		do {
			const params = new URLSearchParams();
			params.set("per_page", "100");
			if (query) params.set("query", query);
			if (cursor) params.set("cursor", cursor);
			const response = await this.request(apiPath`/organizations/${organizationSlug}/repos/` + `?${params.toString()}`, { method: "GET" }, opts);
			const body = await this.parseJsonResponse(response);
			repos.push(...RepositoryListSchema.parse(body));
			cursor = getNextCursor(response.headers.get("link"));
		} while (cursor);
		return repos;
	}
	async linkProjectRepository({ organizationSlug, projectSlug, repository, provider }, opts) {
		const body = await this.requestJSON(apiPath`/organizations/${organizationSlug}/code-mappings/bulk/`, {
			method: "POST",
			body: JSON.stringify({
				project: projectSlug,
				repository,
				provider,
				mappings: [{
					stackRoot: "",
					sourceRoot: ""
				}]
			})
		}, opts);
		return ProjectRepositoryMappingSchema.parse(body);
	}
	async listIssueAlertRules(params, opts) {
		const page = await this.listIssueAlertRulesPage(params, opts);
		return page.rules;
	}
	async listIssueAlertRulesPage({ organizationSlug, projectSlug, query, cursor, limit }, opts) {
		const rules = [];
		const targetLimit = limit ?? 100;
		let currentCursor = cursor;
		let nextCursor = null;
		do {
			const searchQuery = new URLSearchParams();
			searchQuery.append("projectSlug", projectSlug);
			searchQuery.set("sortBy", "-id");
			if (query) searchQuery.set("query", formatWorkflowNameQuery(query));
			if (currentCursor) searchQuery.set("cursor", currentCursor);
			if (limit !== void 0) searchQuery.set("per_page", String(targetLimit - rules.length));
			const response = await this.request(apiPath`/organizations/${organizationSlug}/workflows/` + `?${searchQuery.toString()}`, void 0, opts);
			const body = await this.parseJsonResponse(response);
			const attachedRules = filterAttachedIssueAlertRules(IssueAlertRuleListSchema.parse(body));
			const remaining = targetLimit - rules.length;
			if (attachedRules.length > remaining) {
				rules.push(...attachedRules.slice(0, remaining));
				nextCursor = null;
				break;
			}
			rules.push(...attachedRules);
			nextCursor = getNextCursor(response.headers.get("link"));
			currentCursor = nextCursor;
		} while (rules.length < targetLimit && nextCursor);
		return {
			rules,
			nextCursor
		};
	}
	async getIssueAlertRule({ organizationSlug, projectSlug, ruleId }, opts) {
		const searchQuery = new URLSearchParams();
		searchQuery.append("projectSlug", projectSlug);
		searchQuery.append("id", String(ruleId));
		searchQuery.set("per_page", "1");
		const response = await this.request(apiPath`/organizations/${organizationSlug}/workflows/` + `?${searchQuery.toString()}`, void 0, opts);
		const rules = filterAttachedIssueAlertRules(IssueAlertRuleListSchema.parse(await this.parseJsonResponse(response)));
		const rule = rules[0];
		if (!rule) throw new ApiNotFoundError("Workflow not found", void 0, void 0, "workflow", String(ruleId));
		return rule;
	}
	async listMetricAlertRules(params, opts) {
		const page = await this.listMetricAlertRulesPage(params, opts);
		return page.rules;
	}
	async listMetricAlertRulesPage({ organizationSlug, projectSlug, query, cursor, limit }, opts) {
		const project = projectSlug ? await this.getProject({
			organizationSlug,
			projectSlugOrId: projectSlug
		}, opts) : void 0;
		if (query) {
			const body$1 = await this.listCombinedAlertRules({
				organizationSlug,
				name: query,
				alertType: "alert_rule",
				projectId: project?.id,
				cursor,
				limit
			}, opts);
			return {
				rules: MetricAlertRuleListSchema.parse(body$1.data),
				nextCursor: body$1.nextCursor
			};
		}
		const searchQuery = new URLSearchParams();
		if (cursor) searchQuery.set("cursor", cursor);
		if (limit !== void 0) searchQuery.set("per_page", String(limit));
		if (project) searchQuery.set("project", String(project.id));
		const queryString = searchQuery.toString();
		const path = apiPath`/organizations/${organizationSlug}/alert-rules/`;
		const response = await this.request(`${path}${queryString ? `?${queryString}` : ""}`, void 0, opts);
		const body = await this.parseJsonResponse(response);
		return {
			rules: MetricAlertRuleListSchema.parse(body),
			nextCursor: getNextCursor(response.headers.get("link"))
		};
	}
	async getMetricAlertRule({ organizationSlug, ruleId }, opts) {
		const path = apiPath`/organizations/${organizationSlug}/alert-rules/${ruleId}/`;
		const body = await this.requestJSON(path, void 0, opts);
		return MetricAlertRuleSchema.parse(body);
	}
	/**
	* Uses Sentry's combined-rules endpoint for metric alert name search.
	*/
	async listCombinedAlertRules({ organizationSlug, name, alertType, projectId, cursor, limit }, opts) {
		const searchQuery = new URLSearchParams();
		searchQuery.set("name", name);
		searchQuery.append("alertType", alertType);
		if (projectId !== void 0) searchQuery.append("project", String(projectId));
		if (cursor) searchQuery.set("cursor", cursor);
		if (limit !== void 0) searchQuery.set("per_page", String(limit));
		const response = await this.request(apiPath`/organizations/${organizationSlug}/combined-rules/` + `?${searchQuery.toString()}`, void 0, opts);
		return {
			data: await this.parseJsonResponse(response),
			nextCursor: getNextCursor(response.headers.get("link"))
		};
	}
	/**
	* Lists teams assigned to a project.
	*
	* @param params Query parameters
	* @param params.organizationSlug Organization identifier
	* @param params.projectSlug Project identifier
	* @param opts Request options
	* @returns Array of teams assigned to the project
	*/
	async listProjectTeams({ organizationSlug, projectSlug }, opts) {
		const teams = [];
		let cursor = null;
		do {
			const queryParams = new URLSearchParams();
			queryParams.set("per_page", "100");
			if (cursor) queryParams.set("cursor", cursor);
			const response = await this.request(apiPath`/projects/${organizationSlug}/${projectSlug}/teams/` + `?${queryParams.toString()}`, void 0, opts);
			const body = await this.parseJsonResponse(response);
			teams.push(...TeamListSchema.parse(body));
			cursor = getNextCursor(response.headers.get("link"));
		} while (cursor);
		return teams;
	}
	/**
	* Assigns a team to a project.
	*
	* @param params Assignment parameters
	* @param params.organizationSlug Organization identifier
	* @param params.projectSlug Project identifier
	* @param params.teamSlug Team identifier to assign
	* @param opts Request options
	*/
	async addTeamToProject({ organizationSlug, projectSlug, teamSlug }, opts) {
		await this.request(apiPath`/projects/${organizationSlug}/${projectSlug}/teams/${teamSlug}/`, {
			method: "POST",
			body: JSON.stringify({})
		}, opts);
	}
	/**
	* Removes a team from a project.
	*
	* @param params Assignment parameters
	* @param params.organizationSlug Organization identifier
	* @param params.projectSlug Project identifier
	* @param params.teamSlug Team identifier to remove
	* @param opts Request options
	*/
	async removeTeamFromProject({ organizationSlug, projectSlug, teamSlug }, opts) {
		await this.request(apiPath`/projects/${organizationSlug}/${projectSlug}/teams/${teamSlug}/`, { method: "DELETE" }, opts);
	}
	/**
	* Creates a new client key (DSN) for a project.
	*
	* Client keys are used to identify and authenticate SDK requests to Sentry.
	*
	* @param params Key creation parameters
	* @param params.organizationSlug Organization identifier
	* @param params.projectSlug Project identifier
	* @param params.name Human-readable name for the key (optional)
	* @param opts Request options
	* @returns Created client key with DSN information
	*
	* @example
	* ```typescript
	* const key = await apiService.createClientKey({
	*   organizationSlug: "my-org",
	*   projectSlug: "my-project",
	*   name: "Production"
	* });
	* console.log(`DSN: ${key.dsn.public}`);
	* ```
	*/
	async createClientKey({ organizationSlug, projectSlug, name }, opts) {
		const body = await this.requestJSON(apiPath`/projects/${organizationSlug}/${projectSlug}/keys/`, {
			method: "POST",
			body: JSON.stringify({ name })
		}, opts);
		return ClientKeySchema.parse(body);
	}
	/**
	* Updates a client key (DSN) for a project.
	*
	* @param params Key update parameters
	* @param params.organizationSlug Organization identifier
	* @param params.projectSlug Project identifier
	* @param params.keyId The ID of the key to update
	* @param params.name Human-readable name for the key (optional)
	* @param params.isActive Activate or deactivate the client key (optional)
	* @param params.rateLimit Applies a rate limit to cap the number of errors accepted during a given time window (optional, null to disable)
	* @param params.browserSdkVersion Sentry Javascript SDK version to use (optional)
	* @param params.dynamicSdkLoaderOptions Configures options for the Javascript Loader Script (optional)
	* @param opts Request options
	* @returns Updated client key with DSN information
	*/
	async updateClientKey({ organizationSlug, projectSlug, keyId, name, isActive, rateLimit, browserSdkVersion, dynamicSdkLoaderOptions }, opts) {
		const updateData = {};
		if (name !== void 0) updateData.name = name;
		if (isActive !== void 0) updateData.isActive = isActive;
		if (rateLimit !== void 0) updateData.rateLimit = rateLimit;
		if (browserSdkVersion !== void 0) updateData.browserSdkVersion = browserSdkVersion;
		if (dynamicSdkLoaderOptions !== void 0) updateData.dynamicSdkLoaderOptions = dynamicSdkLoaderOptions;
		const body = await this.requestJSON(apiPath`/projects/${organizationSlug}/${projectSlug}/keys/${keyId}/`, {
			method: "PUT",
			body: JSON.stringify(updateData)
		}, opts);
		return ClientKeySchema.parse(body);
	}
	/**
	* Lists all client keys (DSNs) for a project.
	*
	* @param params Query parameters
	* @param params.organizationSlug Organization identifier
	* @param params.projectSlug Project identifier
	* @param opts Request options
	* @returns Array of client keys with DSN information
	*/
	async listClientKeys({ organizationSlug, projectSlug }, opts) {
		const body = await this.requestJSON(apiPath`/projects/${organizationSlug}/${projectSlug}/keys/`, void 0, opts);
		return ClientKeyListSchema.parse(body);
	}
	/**
	* Lists releases for an organization or specific project.
	*
	* @param params Query parameters
	* @param params.organizationSlug Organization identifier
	* @param params.projectSlug Project identifier (optional, scopes to specific project)
	* @param params.query Search query for filtering releases
	* @param opts Request options
	* @returns Array of releases with deployment and commit information
	*
	* @example
	* ```typescript
	* // All releases for organization
	* const releases = await apiService.listReleases({
	*   organizationSlug: "my-org"
	* });
	*
	* // Search for specific version
	* const filtered = await apiService.listReleases({
	*   organizationSlug: "my-org",
	*   query: "v1.2.3"
	* });
	* ```
	*/
	async listReleases({ organizationSlug, projectSlug, query, limit }, opts) {
		const searchQuery = new URLSearchParams();
		if (query) searchQuery.set("query", query);
		if (limit !== void 0) searchQuery.set("per_page", String(limit));
		const path = projectSlug ? apiPath`/projects/${organizationSlug}/${projectSlug}/releases/` : apiPath`/organizations/${organizationSlug}/releases/`;
		const body = await this.requestJSON(searchQuery.toString() ? `${path}?${searchQuery.toString()}` : path, void 0, opts);
		return ReleaseListSchema.parse(body);
	}
	async getReleaseDetails({ organizationSlug, releaseVersion, projectSlugOrId, includeHealth }, opts) {
		const searchQuery = new URLSearchParams();
		if (includeHealth) searchQuery.set("health", "1");
		const path = projectSlugOrId ? apiPath`/projects/${organizationSlug}/${projectSlugOrId}/releases/${releaseVersion}/` : apiPath`/organizations/${organizationSlug}/releases/${releaseVersion}/`;
		const body = await this.requestJSON(searchQuery.toString() ? `${path}?${searchQuery.toString()}` : path, void 0, opts);
		return ReleaseDetailsSchema.parse(body);
	}
	async listReleaseDeploys({ organizationSlug, releaseVersion, projectSlugOrId, limit }, opts) {
		const searchQuery = new URLSearchParams();
		if (projectSlugOrId) searchQuery.set(isNumericId(projectSlugOrId) ? "project" : "projectSlug", projectSlugOrId);
		if (limit !== void 0) searchQuery.set("per_page", String(limit));
		const path = apiPath`/organizations/${organizationSlug}/releases/${releaseVersion}/deploys/`;
		const body = await this.requestJSON(searchQuery.toString() ? `${path}?${searchQuery.toString()}` : path, void 0, opts);
		return DeployListSchema.parse(body);
	}
	async listReleaseCommits({ organizationSlug, releaseVersion, projectSlugOrId, limit }, opts) {
		const searchQuery = new URLSearchParams();
		if (limit !== void 0) searchQuery.set("per_page", String(limit));
		const path = projectSlugOrId ? apiPath`/projects/${organizationSlug}/${projectSlugOrId}/releases/${releaseVersion}/commits/` : apiPath`/organizations/${organizationSlug}/releases/${releaseVersion}/commits/`;
		const body = await this.requestJSON(searchQuery.toString() ? `${path}?${searchQuery.toString()}` : path, void 0, opts);
		return CommitListSchema.parse(body);
	}
	async listMonitors({ organizationSlug, projectSlug, environment, owner, query, limit }, opts) {
		const searchQuery = new URLSearchParams();
		if (projectSlug) searchQuery.append("projectSlug", projectSlug);
		if (environment) searchQuery.append("environment", environment);
		if (owner) searchQuery.append("owner", owner);
		if (query) searchQuery.set("query", query);
		if (limit !== void 0) searchQuery.set("per_page", String(limit));
		const body = await this.requestJSON(searchQuery.toString() ? apiPath`/organizations/${organizationSlug}/monitors/` + `?${searchQuery.toString()}` : apiPath`/organizations/${organizationSlug}/monitors/`, void 0, opts);
		return MonitorListSchema.parse(body);
	}
	async getMonitorDetails({ organizationSlug, projectSlug, monitorSlug, environment }, opts) {
		const searchQuery = new URLSearchParams();
		if (environment) searchQuery.append("environment", environment);
		const path = projectSlug ? apiPath`/projects/${organizationSlug}/${projectSlug}/monitors/${monitorSlug}/` : apiPath`/organizations/${organizationSlug}/monitors/${monitorSlug}/`;
		const body = await this.requestJSON(searchQuery.toString() ? `${path}?${searchQuery.toString()}` : path, void 0, opts);
		return MonitorSchema.parse(body);
	}
	async listMonitorCheckIns({ organizationSlug, projectSlug, monitorSlug, environment, statsPeriod, start, end, limit }, opts) {
		const searchQuery = new URLSearchParams();
		if (environment) searchQuery.append("environment", environment);
		if (limit !== void 0) searchQuery.set("per_page", String(limit));
		const normalizedStatsPeriod = normalizeStatsPeriod(statsPeriod);
		const effectiveStatsPeriod = start || end ? normalizedStatsPeriod : normalizedStatsPeriod ?? "24h";
		if (effectiveStatsPeriod) parseStatsPeriod(effectiveStatsPeriod);
		this.applyTimeParams(searchQuery, effectiveStatsPeriod, start, end);
		const path = projectSlug ? apiPath`/projects/${organizationSlug}/${projectSlug}/monitors/${monitorSlug}/checkins/` : apiPath`/organizations/${organizationSlug}/monitors/${monitorSlug}/checkins/`;
		const body = await this.requestJSON(searchQuery.toString() ? `${path}?${searchQuery.toString()}` : path, void 0, opts);
		return MonitorCheckInListSchema.parse(body);
	}
	async getMonitorStats({ organizationSlug, projectSlug, monitorSlug, environment, statsPeriod, start, end, rollup }, opts) {
		const searchQuery = new URLSearchParams();
		if (environment) searchQuery.append("environment", environment);
		const normalizedStatsPeriod = normalizeStatsPeriod(statsPeriod);
		const effectiveStatsPeriod = start || end ? normalizedStatsPeriod : normalizedStatsPeriod ?? "24h";
		this.applyStatsMixinTimeParams(searchQuery, effectiveStatsPeriod, start, end, rollup);
		const path = projectSlug ? apiPath`/projects/${organizationSlug}/${projectSlug}/monitors/${monitorSlug}/stats/` : apiPath`/organizations/${organizationSlug}/monitors/${monitorSlug}/stats/`;
		const body = await this.requestJSON(searchQuery.toString() ? `${path}?${searchQuery.toString()}` : path, void 0, opts);
		return MonitorStatsSchema.parse(body);
	}
	/**
	* List uptime monitors for an organization.
	*
	* GET /organizations/{org}/uptime/
	* Source: src/sentry/uptime/endpoints/organiation_uptime_alert_index.py
	*/
	async listUptimeMonitors({ organizationSlug, projectSlug, environment, owner, query, limit }, opts) {
		const searchQuery = new URLSearchParams();
		if (projectSlug) {
			const project = await this.getProject({
				organizationSlug,
				projectSlugOrId: projectSlug
			}, opts);
			searchQuery.append("project", String(project.id));
		}
		if (environment) searchQuery.append("environment", environment);
		if (owner) searchQuery.append("owner", owner);
		if (query) searchQuery.set("query", query);
		if (limit !== void 0) searchQuery.set("per_page", String(limit));
		const path = apiPath`/organizations/${organizationSlug}/uptime/`;
		const body = await this.requestJSON(searchQuery.toString() ? `${path}?${searchQuery.toString()}` : path, void 0, opts);
		return UptimeMonitorListSchema.parse(body);
	}
	/**
	* Get a single uptime monitor.
	*
	* GET /projects/{org}/{project}/uptime/{id}/
	* Source: src/sentry/uptime/endpoints/project_uptime_alert_details.py
	*/
	async getUptimeMonitorDetails({ organizationSlug, projectSlug, uptimeMonitorId }, opts) {
		const body = await this.requestJSON(apiPath`/projects/${organizationSlug}/${projectSlug}/uptime/${uptimeMonitorId}/`, void 0, opts);
		return UptimeMonitorSchema.parse(body);
	}
	/**
	* List recent uptime checks for a monitor.
	*
	* GET /projects/{org}/{project}/uptime/{id}/checks/
	* Source: src/sentry/uptime/endpoints/project_uptime_alert_checks_index.py
	*/
	async listUptimeMonitorChecks({ organizationSlug, projectSlug, uptimeMonitorId, statsPeriod, start, end, limit }, opts) {
		const searchQuery = new URLSearchParams();
		if (limit !== void 0) searchQuery.set("per_page", String(limit));
		const normalizedStatsPeriod = normalizeStatsPeriod(statsPeriod);
		const effectiveStatsPeriod = start || end ? normalizedStatsPeriod : normalizedStatsPeriod ?? "24h";
		if (effectiveStatsPeriod) parseStatsPeriod(effectiveStatsPeriod);
		this.applyTimeParams(searchQuery, effectiveStatsPeriod, start, end);
		const path = apiPath`/projects/${organizationSlug}/${projectSlug}/uptime/${uptimeMonitorId}/checks/`;
		const body = await this.requestJSON(searchQuery.toString() ? `${path}?${searchQuery.toString()}` : path, void 0, opts);
		return UptimeCheckListSchema.parse(body);
	}
	/**
	* Create an uptime monitor.
	*
	* POST /projects/{org}/{project}/uptime/
	* Source: src/sentry/uptime/endpoints/project_uptime_alert_index.py
	* Body fields verified from UptimeMonitorValidator (camelCase via CamelSnakeSerializer).
	*/
	async createUptimeMonitor({ organizationSlug, projectSlug, name, url, intervalSeconds, timeoutMs, method, headers, body, assertion, status, owner, environment, traceSampling, responseCaptureEnabled, recoveryThreshold, downtimeThreshold }, opts) {
		const payload = {
			name,
			url,
			intervalSeconds,
			timeoutMs
		};
		if (method !== void 0) payload.method = method;
		if (headers !== void 0) payload.headers = headers;
		if (body !== void 0) payload.body = body;
		if (assertion !== void 0) payload.assertion = assertion;
		if (status !== void 0) payload.status = status;
		if (owner !== void 0) payload.owner = owner;
		if (environment !== void 0) payload.environment = environment;
		if (traceSampling !== void 0) payload.traceSampling = traceSampling;
		if (responseCaptureEnabled !== void 0) payload.responseCaptureEnabled = responseCaptureEnabled;
		if (recoveryThreshold !== void 0) payload.recoveryThreshold = recoveryThreshold;
		if (downtimeThreshold !== void 0) payload.downtimeThreshold = downtimeThreshold;
		const responseBody = await this.requestJSON(apiPath`/projects/${organizationSlug}/${projectSlug}/uptime/`, {
			method: "POST",
			body: JSON.stringify(payload)
		}, opts);
		return UptimeMonitorSchema.parse(responseBody);
	}
	/**
	* Update an uptime monitor.
	*
	* PUT /projects/{org}/{project}/uptime/{id}/
	* Source: src/sentry/uptime/endpoints/project_uptime_alert_details.py
	*/
	async updateUptimeMonitor({ organizationSlug, projectSlug, uptimeMonitorId, name, url, intervalSeconds, timeoutMs, method, headers, body, assertion, status, owner, environment, traceSampling, responseCaptureEnabled, recoveryThreshold, downtimeThreshold }, opts) {
		const payload = {};
		if (name !== void 0) payload.name = name;
		if (url !== void 0) payload.url = url;
		if (intervalSeconds !== void 0) payload.intervalSeconds = intervalSeconds;
		if (timeoutMs !== void 0) payload.timeoutMs = timeoutMs;
		if (method !== void 0) payload.method = method;
		if (headers !== void 0) payload.headers = headers;
		if (body !== void 0) payload.body = body;
		if (assertion !== void 0) payload.assertion = assertion;
		if (status !== void 0) payload.status = status;
		if (owner !== void 0) payload.owner = owner;
		if (environment !== void 0) payload.environment = environment;
		if (traceSampling !== void 0) payload.traceSampling = traceSampling;
		if (responseCaptureEnabled !== void 0) payload.responseCaptureEnabled = responseCaptureEnabled;
		if (recoveryThreshold !== void 0) payload.recoveryThreshold = recoveryThreshold;
		if (downtimeThreshold !== void 0) payload.downtimeThreshold = downtimeThreshold;
		const responseBody = await this.requestJSON(apiPath`/projects/${organizationSlug}/${projectSlug}/uptime/${uptimeMonitorId}/`, {
			method: "PUT",
			body: JSON.stringify(payload)
		}, opts);
		return UptimeMonitorSchema.parse(responseBody);
	}
	/**
	* Delete an uptime monitor.
	*
	* DELETE /projects/{org}/{project}/uptime/{id}/
	* Source: src/sentry/uptime/endpoints/project_uptime_alert_details.py
	* Returns 202 with empty body.
	*/
	async deleteUptimeMonitor({ organizationSlug, projectSlug, uptimeMonitorId }, opts) {
		await this.request(apiPath`/projects/${organizationSlug}/${projectSlug}/uptime/${uptimeMonitorId}/`, { method: "DELETE" }, {
			...opts,
			allowStatuses: [404]
		});
	}
	/**
	* Lists available tags for search queries.
	*
	* Tags represent indexed fields that can be used in Sentry search queries.
	*
	* @param params Query parameters
	* @param params.organizationSlug Organization identifier
	* @param params.dataset Dataset to query tags for ("events", "errors", "replays", or "search_issues")
	* @param params.project Numeric project ID to filter tags
	* @param params.statsPeriod Time range for tag statistics (e.g., "24h", "7d")
	* @param params.useCache Whether to use cached results
	* @param params.useFlagsBackend Whether to use flags backend features
	* @param opts Request options
	* @returns Array of available tags with metadata
	*
	* @example
	* ```typescript
	* const tags = await apiService.listTags({
	*   organizationSlug: "my-org",
	*   dataset: "events",
	*   project: "123456",
	*   statsPeriod: "24h",
	*   useCache: true
	* });
	* tags.forEach(tag => console.log(`${tag.key}: ${tag.name}`));
	* ```
	*/
	async listTags({ organizationSlug, dataset, project, statsPeriod, start, end, useCache, useFlagsBackend }, opts) {
		const searchQuery = new URLSearchParams();
		if (dataset) searchQuery.set("dataset", dataset);
		if (project) searchQuery.set("project", project);
		this.applyTimeParams(searchQuery, statsPeriod, start, end);
		if (useCache !== void 0) searchQuery.set("useCache", useCache ? "1" : "0");
		if (useFlagsBackend !== void 0) searchQuery.set("useFlagsBackend", useFlagsBackend ? "1" : "0");
		const body = await this.requestJSON(searchQuery.toString() ? apiPath`/organizations/${organizationSlug}/tags/` + `?${searchQuery.toString()}` : apiPath`/organizations/${organizationSlug}/tags/`, void 0, opts);
		return TagListSchema.parse(body);
	}
	async searchReplays({ organizationSlug, query, limit, projectId, sort, environment, statsPeriod, start, end, fields }, opts) {
		const searchQuery = new URLSearchParams();
		if (query) searchQuery.set("query", query);
		if (limit !== void 0) searchQuery.set("per_page", String(limit));
		if (projectId) searchQuery.append("project", projectId);
		if (sort) searchQuery.set("sort", sort);
		if (environment) {
			const environments = Array.isArray(environment) ? environment : [environment];
			for (const value of environments) searchQuery.append("environment", value);
		}
		if (fields && fields.length > 0) for (const field of fields) searchQuery.append("field", field);
		this.applyTimeParams(searchQuery, statsPeriod, start, end);
		const body = await this.requestJSON(searchQuery.toString() ? apiPath`/organizations/${organizationSlug}/replays/` + `?${searchQuery.toString()}` : apiPath`/organizations/${organizationSlug}/replays/`, void 0, opts);
		return ReplayListResponseSchema.parse(body).data;
	}
	/**
	* Lists trace item attributes available for search queries.
	*
	* Returns all available fields/attributes that can be used in event searches,
	* including both built-in fields and custom tags.
	*
	* @param params Query parameters
	* @param params.organizationSlug Organization identifier
	* @param params.itemType Item type to query attributes for ("spans", "logs", or "tracemetrics")
	* @param params.project Numeric project ID to filter attributes
	* @param params.statsPeriod Time range for attribute statistics (e.g., "24h", "7d")
	* @param params.attributeTypes Optional attribute types to keep in the response
	* @param opts Request options
	* @returns Array of available attributes with metadata including type
	*/
	async listTraceItemAttributes({ organizationSlug, itemType = "spans", project, statsPeriod, start, end, attributeTypes, substringMatch, query }, opts) {
		const attributes = await this.fetchTraceItemAttributes(organizationSlug, itemType, project, statsPeriod, start, end, substringMatch, query, opts);
		if (!attributeTypes || attributeTypes.length === 0) return attributes;
		const allowedTypes = new Set(attributeTypes);
		return attributes.filter((attribute) => allowedTypes.has(attribute.type));
	}
	async validateEvents({ organizationSlug, dataset = "spans", fields, query, orderby, project, environment, statsPeriod, start, end }, opts) {
		const queryParams = new URLSearchParams();
		queryParams.set("dataset", normalizeEventsDataset(dataset));
		if (project) queryParams.set("project", project);
		if (query) queryParams.set("query", query);
		if (environment) {
			const environments = Array.isArray(environment) ? environment : [environment];
			for (const value of environments) queryParams.append("environment", value);
		}
		this.applyTimeParams(queryParams, statsPeriod, start, end);
		for (const field of fields ?? []) queryParams.append("field", field);
		for (const value of orderby ?? []) queryParams.append("orderby", value);
		const response = await this.request(apiPath`/organizations/${organizationSlug}/events/validate/` + `?${queryParams.toString()}`, void 0, {
			...opts,
			allowStatuses: [400]
		});
		const body = await this.parseJsonResponse(response);
		return EventsValidationResponseSchema.parse(body);
	}
	async fetchTraceItemAttributes(organizationSlug, itemType, project, statsPeriod, start, end, substringMatch, query, opts) {
		const queryParams = new URLSearchParams();
		queryParams.set("itemType", itemType);
		if (project) queryParams.set("project", project);
		if (substringMatch) queryParams.set("substringMatch", substringMatch);
		if (query) queryParams.set("query", query);
		this.applyTimeParams(queryParams, statsPeriod, start, end);
		const url = apiPath`/organizations/${organizationSlug}/trace-items/attributes/` + `?${queryParams.toString()}`;
		const body = await this.requestJSON(url, void 0, opts);
		return TraceItemAttributeListSchema.parse(body);
	}
	/**
	* Lists issues within an organization or project.
	*
	* Issues represent groups of similar errors or problems in your application.
	* Supports Sentry's powerful query syntax for filtering and sorting.
	*
	* @param params Query parameters
	* @param params.organizationSlug Organization identifier
	* @param params.projectSlug Project identifier (optional, scopes to specific project)
	* @param params.query Sentry search query (e.g., "is:unresolved browser:chrome")
	* @param params.sortBy Sort order ("user", "freq", "date", "new", "recommended")
	* @param opts Request options
	* @returns Array of issues with metadata and statistics
	*
	* @example
	* ```typescript
	* // Recent unresolved issues
	* const issues = await apiService.listIssues({
	*   organizationSlug: "my-org",
	*   query: "is:unresolved",
	*   sortBy: "date"
	* });
	*
	* // High-frequency errors in specific project
	* const critical = await apiService.listIssues({
	*   organizationSlug: "my-org",
	*   projectId: "123456",
	*   query: "level:error",
	*   sortBy: "freq"
	* });
	* ```
	*/
	async listIssues({ organizationSlug, projectId, query, sortBy, limit = 10, statsPeriod = DEFAULT_SEARCH_ISSUES_PERIOD }, opts) {
		const sentryQuery = [];
		if (query) sentryQuery.push(query);
		const queryParams = new URLSearchParams();
		queryParams.set("limit", String(limit));
		if (sortBy) queryParams.set("sort", sortBy);
		queryParams.set("statsPeriod", statsPeriod ?? DEFAULT_SEARCH_ISSUES_PERIOD);
		queryParams.set("query", sentryQuery.join(" "));
		if (projectId) queryParams.append("project", projectId);
		queryParams.append("collapse", "unhandled");
		const apiUrl = apiPath`/organizations/${organizationSlug}/issues/` + `?${queryParams.toString()}`;
		const body = await this.requestJSON(apiUrl, void 0, opts);
		return IssueListSchema.parse(body);
	}
	async getIssue({ organizationSlug, issueId }, opts) {
		const body = await this.requestJSON(apiPath`/organizations/${organizationSlug}/issues/${issueId}/`, void 0, opts);
		return IssueSchema.parse(body);
	}
	/**
	* Retrieves tag value distribution for a specific issue.
	*
	* Returns aggregate counts of unique tag values, useful for understanding
	* how an issue is distributed across different tag values (e.g., URLs,
	* browsers, environments).
	*
	* @param params Query parameters
	* @param params.organizationSlug Organization identifier
	* @param params.issueId Issue identifier (short ID or numeric ID)
	* @param params.tagKey Tag key to get values for (e.g., "url", "browser", "environment")
	* @param opts Request options
	* @returns Tag value distribution with counts and percentages
	*
	* @example
	* ```typescript
	* const tagValues = await apiService.getIssueTagValues({
	*   organizationSlug: "my-org",
	*   issueId: "PROJECT-123",
	*   tagKey: "url"
	* });
	* console.log(`Total unique values: ${tagValues.totalValues}`);
	* tagValues.topValues.forEach(v => console.log(`${v.value}: ${v.count}`));
	* ```
	*/
	async getIssueTagValues({ organizationSlug, issueId, tagKey }, opts) {
		const body = await this.requestJSON(apiPath`/organizations/${organizationSlug}/issues/${issueId}/tags/${tagKey}/`, void 0, opts);
		return IssueTagValuesSchema.parse(body);
	}
	/**
	* Retrieves external issue links for a specific issue.
	*
	* Returns links to external issue tracking systems (Jira, GitHub Issues,
	* GitLab, etc.) that have been associated with this Sentry issue.
	*
	* @param params Query parameters
	* @param params.organizationSlug Organization identifier
	* @param params.issueId Issue identifier (short ID or numeric ID)
	* @param opts Request options
	* @returns Array of external issue links with service type and URL
	*/
	async getIssueExternalLinks({ organizationSlug, issueId }, opts) {
		const body = await this.requestJSON(apiPath`/organizations/${organizationSlug}/issues/${issueId}/external-issues/`, void 0, opts);
		return ExternalIssueListSchema.parse(body);
	}
	/**
	* Retrieves issue user reports and returns the next Sentry cursor when another page exists.
	*/
	async getIssueUserReports({ organizationSlug, issueId, cursor, limit }, opts) {
		const searchQuery = new URLSearchParams();
		if (cursor) searchQuery.set("cursor", cursor);
		if (limit !== void 0) searchQuery.set("per_page", String(limit));
		const path = apiPath`/organizations/${organizationSlug}/issues/${issueId}/user-reports/`;
		const response = await this.request(searchQuery.toString() ? `${path}?${searchQuery.toString()}` : path, void 0, opts);
		const body = await this.parseJsonResponse(response);
		return {
			reports: UserReportListSchema.parse(body),
			nextCursor: getNextCursor(response.headers.get("link"))
		};
	}
	async getEventForIssue({ organizationSlug, issueId, eventId }, opts) {
		const body = await this.requestJSON(apiPath`/organizations/${organizationSlug}/issues/${issueId}/events/${eventId}/`, void 0, opts);
		const parseResult = EventSchema.safeParse(body);
		if (parseResult.success) {
			const rawEvent = parseResult.data;
			if (rawEvent.type === "error" || rawEvent.type === "default") return rawEvent;
			if (rawEvent.type === "transaction") return rawEvent;
			if (rawEvent.type === "generic") return rawEvent;
			if (rawEvent.type === "csp") return rawEvent;
			const eventType$1 = typeof rawEvent.type === "string" ? rawEvent.type : String(rawEvent.type);
			logIssue(`Unsupported event type: ${eventType$1}`, { extra: {
				eventType: eventType$1,
				eventId: rawEvent.id
			} });
			return rawEvent;
		}
		const bodyObj = body;
		const eventType = typeof bodyObj.type === "string" ? bodyObj.type : String(bodyObj.type);
		logIssue(`Event failed schema validation: ${eventType}`, { extra: {
			eventType,
			eventId: bodyObj.id,
			validationError: parseResult.error.message,
			validationIssues: parseResult.error.issues
		} });
		throw new ApiValidationError(`Event failed schema validation: ${parseResult.error.message}`, void 0, void 0, body);
	}
	async getLatestEventForIssue({ organizationSlug, issueId }, opts) {
		return this.getEventForIssue({
			organizationSlug,
			issueId,
			eventId: "latest"
		}, opts);
	}
	/**
	* Resolves a stack frame through the project's configured source code mapping.
	* The upstream endpoint verifies the mapped path with the SCM integration.
	*/
	async getStacktraceLink({ organizationSlug, projectSlug, file, platform, lineNo, absPath, module, package: framePackage, commitId, groupId, sdkName, signal }, opts) {
		const query = new URLSearchParams({ file });
		if (platform) query.set("platform", platform);
		if (lineNo !== void 0) query.set("lineNo", String(lineNo));
		if (absPath) query.set("absPath", absPath);
		if (module) query.set("module", module);
		if (framePackage) query.set("package", framePackage);
		if (commitId) query.set("commitId", commitId);
		if (groupId) query.set("groupId", groupId);
		if (sdkName) query.set("sdkName", sdkName);
		const body = await this.requestJSON(apiPath`/projects/${organizationSlug}/${projectSlug}/stacktrace-link/` + `?${query.toString()}`, { signal }, opts);
		return StacktraceLinkSchema.parse(body);
	}
	/**
	* Lists events for a specific issue.
	* Uses the issue-specific endpoint which already filters by issue ID.
	*
	* @see https://docs.sentry.io/api/events/list-an-issues-events/
	*/
	async listEventsForIssue({ organizationSlug, issueId, query, limit = 50, sort, statsPeriod, start, end, full = false }, opts) {
		const params = new URLSearchParams();
		if (query) params.append("query", query);
		params.append("per_page", String(limit));
		if (sort) params.append("sort", sort);
		if (statsPeriod) params.append("statsPeriod", statsPeriod);
		else if (start && end) {
			params.append("start", start);
			params.append("end", end);
		}
		if (full) params.append("full", "true");
		const apiUrl = apiPath`/organizations/${organizationSlug}/issues/${issueId}/events/` + `?${params.toString()}`;
		return await this.requestJSON(apiUrl, void 0, opts);
	}
	async listEventAttachments({ organizationSlug, projectSlug, eventId }, opts) {
		const body = await this.requestJSON(apiPath`/projects/${organizationSlug}/${projectSlug}/events/${eventId}/attachments/`, void 0, opts);
		return EventAttachmentListSchema.parse(body);
	}
	async getEventAttachment({ organizationSlug, projectSlug, eventId, attachmentId }, opts) {
		const attachmentsData = await this.requestJSON(apiPath`/projects/${organizationSlug}/${projectSlug}/events/${eventId}/attachments/`, void 0, opts);
		const attachments = EventAttachmentListSchema.parse(attachmentsData);
		const attachment = attachments.find((att) => att.id === attachmentId);
		if (!attachment) throw new ApiNotFoundError(`Attachment with ID ${attachmentId} not found for event ${eventId}`);
		const downloadUrl = apiPath`/projects/${organizationSlug}/${projectSlug}/events/${eventId}/attachments/${attachmentId}/` + `?download=1`;
		const downloadResponse = await this.request(downloadUrl, { method: "GET" }, opts);
		const contentType = downloadResponse.headers.get("content-type")?.split(";")[0].trim() || attachment.mimetype || "application/octet-stream";
		return {
			attachment,
			downloadUrl: downloadResponse.url,
			filename: attachment.name,
			blob: await downloadResponse.blob(),
			contentType
		};
	}
	async getReplayDetails({ organizationSlug, replayId }, opts) {
		const body = await this.requestJSON(apiPath`/organizations/${organizationSlug}/replays/${replayId}/`, void 0, opts);
		return z.object({ data: ReplayDetailsSchema }).parse(body).data;
	}
	async listReplayIdsForIssue({ organizationSlug, issueId, dataSource }, opts) {
		const normalizedIssueId = String(issueId);
		const queryParams = new URLSearchParams();
		queryParams.set("returnIds", "true");
		queryParams.set("query", `issue.id:[${normalizedIssueId}]`);
		queryParams.set("data_source", dataSource);
		queryParams.set("statsPeriod", "90d");
		queryParams.append("project", "-1");
		const body = await this.requestJSON(apiPath`/organizations/${organizationSlug}/replay-count/` + `?${queryParams.toString()}`, void 0, opts);
		const replayIdsByResource = ReplayIdsByResourceSchema.parse(body);
		return replayIdsByResource[normalizedIssueId] ?? [];
	}
	async getReplayRecordingSegments({ organizationSlug, projectSlugOrId, replayId }, opts) {
		const body = await this.requestJSON(apiPath`/projects/${organizationSlug}/${projectSlugOrId}/replays/${replayId}/recording-segments/` + `?download=true`, void 0, opts);
		return ReplayRecordingSegmentsSchema.parse(body);
	}
	async updateIssue({ organizationSlug, issueId, status, assignedTo, substatus, ignoreDuration, ignoreCount, ignoreWindow, ignoreUserCount, ignoreUserWindow }, opts) {
		const updateData = {};
		if (status !== void 0) updateData.status = status;
		if (assignedTo !== void 0) updateData.assignedTo = assignedTo;
		if (substatus !== void 0) updateData.substatus = substatus;
		if (ignoreDuration !== void 0) updateData.ignoreDuration = ignoreDuration;
		if (ignoreCount !== void 0) updateData.ignoreCount = ignoreCount;
		if (ignoreWindow !== void 0) updateData.ignoreWindow = ignoreWindow;
		if (ignoreUserCount !== void 0) updateData.ignoreUserCount = ignoreUserCount;
		if (ignoreUserWindow !== void 0) updateData.ignoreUserWindow = ignoreUserWindow;
		const body = await this.requestJSON(apiPath`/organizations/${organizationSlug}/issues/${issueId}/`, {
			method: "PUT",
			body: JSON.stringify(updateData)
		}, opts);
		return IssueSchema.parse(body);
	}
	async createIssueComment({ organizationSlug, issueId, text }, opts) {
		const body = await this.requestJSON(apiPath`/organizations/${organizationSlug}/issues/${issueId}/notes/`, {
			method: "POST",
			body: JSON.stringify({ text })
		}, opts);
		return IssueCommentSchema.parse(body);
	}
	async getIssueActivity({ organizationSlug, issueId }, opts) {
		const body = await this.requestJSON(apiPath`/organizations/${organizationSlug}/issues/${issueId}/activities/`, void 0, opts);
		return IssueActivityListResponseSchema.parse(body).activity;
	}
	async listIssueComments({ organizationSlug, issueId, limit }, opts) {
		const searchQuery = new URLSearchParams();
		if (limit !== void 0) searchQuery.set("per_page", String(limit));
		const path = apiPath`/organizations/${organizationSlug}/issues/${issueId}/notes/`;
		const body = await this.requestJSON(searchQuery.toString() ? `${path}?${searchQuery.toString()}` : path, void 0, opts);
		return IssueCommentListSchema.parse(body);
	}
	async searchErrors({ organizationSlug, projectSlug, filename, transaction, query, sortBy = "last_seen" }, opts) {
		const sentryQuery = [];
		if (filename) sentryQuery.push(`stack.filename:"*${filename.replace(/"/g, "\\\"")}"`);
		if (transaction) sentryQuery.push(`transaction:"${transaction.replace(/"/g, "\\\"")}"`);
		if (query) sentryQuery.push(query);
		if (projectSlug) sentryQuery.push(`project:${projectSlug}`);
		const queryParams = new URLSearchParams();
		queryParams.set("dataset", "errors");
		queryParams.set("per_page", "10");
		queryParams.set("sort", `-${sortBy === "last_seen" ? "last_seen" : "count"}`);
		queryParams.set("statsPeriod", "24h");
		queryParams.append("field", "issue");
		queryParams.append("field", "title");
		queryParams.append("field", "project");
		queryParams.append("field", "last_seen()");
		queryParams.append("field", "count()");
		queryParams.set("query", sentryQuery.join(" "));
		const apiUrl = apiPath`/organizations/${organizationSlug}/events/` + `?${queryParams.toString()}`;
		const body = await this.requestJSON(apiUrl, void 0, opts);
		return ErrorsSearchResponseSchema.parse(body).data;
	}
	async searchSpans({ organizationSlug, projectSlug, transaction, query, sortBy = "timestamp" }, opts) {
		const sentryQuery = ["is_transaction:true"];
		if (transaction) sentryQuery.push(`transaction:"${transaction.replace(/"/g, "\\\"")}"`);
		if (query) sentryQuery.push(query);
		if (projectSlug) sentryQuery.push(`project:${projectSlug}`);
		const queryParams = new URLSearchParams();
		queryParams.set("dataset", "spans");
		queryParams.set("per_page", "10");
		queryParams.set("sort", `-${sortBy === "timestamp" ? "timestamp" : "span.duration"}`);
		queryParams.set("allowAggregateConditions", "0");
		queryParams.set("useRpc", "1");
		queryParams.append("field", "id");
		queryParams.append("field", "trace");
		queryParams.append("field", "span.op");
		queryParams.append("field", "span.description");
		queryParams.append("field", "span.duration");
		queryParams.append("field", "transaction");
		queryParams.append("field", "project");
		queryParams.append("field", "timestamp");
		queryParams.set("query", sentryQuery.join(" "));
		const apiUrl = apiPath`/organizations/${organizationSlug}/events/` + `?${queryParams.toString()}`;
		const body = await this.requestJSON(apiUrl, void 0, opts);
		return SpansSearchResponseSchema.parse(body).data;
	}
	/**
	* Builds query parameters for the legacy Discover API (primarily used by errors dataset).
	*
	* Note: While the API endpoint is the same for all datasets, we maintain separate
	* builders to make future divergence easier and to keep the code organized.
	*/
	buildDiscoverApiQuery(params) {
		const queryParams = new URLSearchParams();
		queryParams.set("per_page", params.limit.toString());
		queryParams.set("query", params.query);
		queryParams.set("dataset", params.dataset ?? "errors");
		this.applyTimeParams(queryParams, params.statsPeriod, params.start, params.end);
		if (params.projectId) queryParams.set("project", params.projectId);
		queryParams.set("sort", params.sort);
		for (const field of params.fields) queryParams.append("field", field);
		queryParams.set("referrer", SENTRY_MCP_SEARCH_EVENTS_REFERRER);
		return queryParams;
	}
	/**
	* Builds query parameters for the modern EAP API (used by spans/logs datasets).
	*
	* Includes dataset-specific parameters like sampling for spans.
	*/
	buildEapApiQuery(params) {
		const queryParams = new URLSearchParams();
		queryParams.set("per_page", params.limit.toString());
		queryParams.set("query", params.query);
		queryParams.set("dataset", params.dataset);
		this.applyTimeParams(queryParams, params.statsPeriod, params.start, params.end);
		if (params.projectId) queryParams.set("project", params.projectId);
		if (params.dataset === "spans") queryParams.set("sampling", "NORMAL");
		queryParams.set("sort", params.sort);
		for (const field of params.fields) queryParams.append("field", field);
		queryParams.set("referrer", SENTRY_MCP_SEARCH_EVENTS_REFERRER);
		return queryParams;
	}
	/**
	* Searches for events in Sentry using the unified events API.
	* This method is used by the search_events tool for semantic search.
	*
	* Routes to the appropriate query builder based on dataset, even though
	* the underlying API endpoint is the same. This separation makes the code
	* cleaner and allows for future API divergence.
	*/
	async searchEvents({ organizationSlug, query, fields, limit = 10, projectId, dataset = "spans", statsPeriod, start, end, sort = "-timestamp" }, opts) {
		let queryParams;
		const normalizedDataset = normalizeEventsDataset(dataset);
		if (normalizedDataset === "errors" || normalizedDataset === "tracemetrics" || normalizedDataset === "profiles") queryParams = this.buildDiscoverApiQuery({
			query,
			fields,
			limit,
			projectId,
			dataset: normalizedDataset,
			statsPeriod,
			start,
			end,
			sort
		});
		else queryParams = this.buildEapApiQuery({
			query,
			fields,
			limit,
			projectId,
			dataset: normalizedDataset,
			statsPeriod,
			start,
			end,
			sort
		});
		const apiUrl = apiPath`/organizations/${organizationSlug}/events/` + `?${queryParams.toString()}`;
		return await this.requestJSON(apiUrl, void 0, opts);
	}
	async startAutofix({ organizationSlug, issueId, eventId, instruction = "" }, opts) {
		const body = await this.requestJSON(apiPath`/organizations/${organizationSlug}/issues/${issueId}/autofix/`, {
			method: "POST",
			body: JSON.stringify({
				event_id: eventId,
				instruction
			})
		}, opts);
		return AutofixRunSchema.parse(body);
	}
	async getAutofixState({ organizationSlug, issueId }, opts) {
		const body = await this.requestJSON(apiPath`/organizations/${organizationSlug}/issues/${issueId}/autofix/`, void 0, opts);
		return AutofixRunStateSchema.parse(body);
	}
	/**
	* Retrieves high-level metadata about a trace.
	*
	* Returns statistics including span counts, error counts, transaction
	* breakdown, and operation type distribution for the specified trace.
	*
	* @param params Query parameters
	* @param params.organizationSlug Organization identifier
	* @param params.traceId Trace identifier (32-character hex string)
	* @param params.statsPeriod Optional stats period (e.g., "14d", "7d")
	* @param opts Request options
	* @returns Trace metadata with statistics
	*
	* @example
	* ```typescript
	* const traceMeta = await apiService.getTraceMeta({
	*   organizationSlug: "my-org",
	*   traceId: "a4d1aae7216b47ff8117cf4e09ce9d0a"
	* });
	* console.log(`Trace has ${traceMeta.span_count} spans`);
	* ```
	*/
	async getTraceMeta({ organizationSlug, traceId, statsPeriod = "14d" }, opts) {
		const queryParams = new URLSearchParams();
		queryParams.set("statsPeriod", statsPeriod);
		const body = await this.requestJSON(apiPath`/organizations/${organizationSlug}/trace-meta/${traceId}/` + `?${queryParams.toString()}`, void 0, opts);
		return TraceMetaSchema.parse(body);
	}
	/**
	* Retrieves the complete trace structure with all spans.
	*
	* Returns the hierarchical trace data including all spans, their timing
	* information, operation details, and nested relationships.
	*
	* @param params Query parameters
	* @param params.organizationSlug Organization identifier
	* @param params.traceId Trace identifier (32-character hex string)
	* @param params.limit Requested span-count hint (default: 1000). The server currently paginates trace data internally and may ignore this value.
	* @param params.project Project filter hint (-1 for all projects). The current organization trace endpoint ignores this value server-side.
	* @param params.statsPeriod Optional stats period (e.g., "14d", "7d")
	* @param opts Request options
	* @returns Complete trace tree structure
	*
	* @example
	* ```typescript
	* const trace = await apiService.getTrace({
	*   organizationSlug: "my-org",
	*   traceId: "a4d1aae7216b47ff8117cf4e09ce9d0a",
	*   limit: 1000
	* });
	* console.log(`Root spans: ${trace.length}`);
	* ```
	*/
	async getTrace({ organizationSlug, traceId, limit = 1e3, project = "-1", statsPeriod = "14d" }, opts) {
		const queryParams = new URLSearchParams();
		queryParams.set("limit", String(limit));
		queryParams.set("project", project);
		queryParams.set("statsPeriod", statsPeriod);
		const body = await this.requestJSON(apiPath`/organizations/${organizationSlug}/trace/${traceId}/` + `?${queryParams.toString()}`, void 0, opts);
		return TraceSchema.parse(body);
	}
	/**
	* Fetches all spans for one AI conversation within a relative or absolute time window.
	*/
	async getAIConversation({ organizationSlug, conversationId, project = "-1", statsPeriod = "30d", start, end, perPage = 1e3, maxPages = 10 }, opts) {
		const spans = [];
		let title = null;
		let cursor = null;
		for (let page = 0; page < maxPages; page++) {
			const queryParams = new URLSearchParams();
			queryParams.set("per_page", String(perPage));
			this.applyTimeParams(queryParams, start || end ? void 0 : statsPeriod, start, end);
			const projects = Array.isArray(project) ? project : [project];
			for (const projectId of projects) queryParams.append("project", projectId);
			if (cursor) queryParams.set("cursor", cursor);
			const response = await this.request(apiPath`/organizations/${organizationSlug}/agents/conversations/${conversationId}/` + `?${queryParams.toString()}`, void 0, opts);
			const body = await this.parseJsonResponse(response);
			const details = AIConversationDetailsResponseSchema.parse(body);
			title = details.title;
			spans.push(...details.spans);
			cursor = getNextCursor(response.headers.get("link"));
			if (!cursor) break;
		}
		return {
			conversationId,
			title,
			spans
		};
	}
	/**
	* Searches Sentry AI Conversations using the conversation list endpoint and
	* returns the current page plus cursor metadata from the response headers.
	*/
	async searchAIConversations({ organizationSlug, query, project, environment, statsPeriod, start, end, limit, cursor }, opts) {
		const queryParams = new URLSearchParams();
		if (query) queryParams.set("query", query);
		if (project) {
			const projects = Array.isArray(project) ? project : [project];
			for (const projectId of projects) queryParams.append("project", projectId);
		}
		if (environment) {
			const environments = Array.isArray(environment) ? environment : [environment];
			for (const environmentName of environments) queryParams.append("environment", environmentName);
		}
		this.applyTimeParams(queryParams, statsPeriod, start, end);
		if (limit !== void 0) queryParams.set("per_page", String(limit));
		if (cursor) queryParams.set("cursor", cursor);
		const response = await this.request(apiPath`/organizations/${organizationSlug}/agents/conversations/` + `?${queryParams.toString()}`, void 0, opts);
		const body = await this.parseJsonResponse(response);
		return {
			conversations: AIConversationSummaryListSchema.parse(body),
			nextCursor: getNextCursor(response.headers.get("link"))
		};
	}
	/**
	* Retrieves flamegraph data for a transaction.
	*
	* Flamegraphs provide pre-aggregated CPU profiling data including:
	* - Unique call stack patterns (samples)
	* - Performance statistics (counts, durations, percentiles)
	* - Frame metadata (file, function, is_application)
	*
	* This is the primary data source for profile analysis as it includes
	* aggregated hot paths and percentile calculations (p75, p95, p99).
	*
	* @param params Query parameters
	* @param params.organizationSlug Organization identifier
	* @param params.projectId Project ID or slug
	* @param params.transactionName Transaction name to analyze (e.g., "/api/users")
	* @param params.statsPeriod Time period for analysis (e.g., "7d", "14d", "30d")
	* @param opts Request options
	* @returns Flamegraph with pre-aggregated profiling data
	*
	* @example
	* ```typescript
	* const flamegraph = await apiService.getFlamegraph({
	*   organizationSlug: "my-org",
	*   projectId: 1,
	*   transactionName: "/api/users",
	*   statsPeriod: "7d"
	* });
	* console.log(`Analyzed ${flamegraph.profiles.length} profiles`);
	* ```
	*/
	async getFlamegraph({ organizationSlug, projectId, transactionName, statsPeriod = "7d" }, opts) {
		const queryParams = new URLSearchParams();
		queryParams.set("project", projectId.toString());
		const escapedTransaction = transactionName.replace(/\\/g, "\\\\").replace(/"/g, "\\\"");
		queryParams.set("query", `event.type:transaction transaction:"${escapedTransaction}"`);
		queryParams.set("statsPeriod", statsPeriod);
		const path = apiPath`/organizations/${organizationSlug}/profiling/flamegraph/` + `?${queryParams.toString()}`;
		const body = await this.requestJSON(path, void 0, opts);
		return FlamegraphSchema.parse(body);
	}
	async getTransactionProfile({ organizationSlug, projectSlugOrId, profileId }, opts) {
		const path = apiPath`/projects/${organizationSlug}/${projectSlugOrId}/profiling/profiles/${profileId}/`;
		const body = await this.requestJSON(path, void 0, opts);
		return TransactionProfileSchema.parse(body);
	}
	/**
	* Retrieves raw profile chunk data for detailed analysis.
	*
	* Profile chunks contain:
	* - frames: All unique stack frames
	* - samples: Individual sample points with timestamps
	* - stacks: Arrays of frame indices forming call stacks
	* - thread_metadata: Information about profiled threads
	*
	* This is used for deep-dive analysis when flamegraph data isn't sufficient.
	* Most use cases should use getFlamegraph() instead, as it provides
	* pre-aggregated data that's easier to work with.
	*
	* @param params Query parameters
	* @param params.organizationSlug Organization identifier
	* @param params.profilerId Profiler ID from flamegraph output
	* @param params.projectId Project ID or slug
	* @param params.start Start time (ISO format)
	* @param params.end End time (ISO format)
	* @param opts Request options
	* @returns Raw profile chunk data
	* @throws ApiNotFoundError if no profile chunk is found
	*
	* @example
	* ```typescript
	* const chunk = await apiService.getProfileChunk({
	*   organizationSlug: "my-org",
	*   profilerId: "041bde57b9844e36b8b7e5734efae5f7",
	*   projectId: 1,
	*   start: "2024-01-01T00:00:00Z",
	*   end: "2024-01-01T01:00:00Z"
	* });
	* console.log(`Chunk has ${chunk.profile.samples.length} samples`);
	* ```
	*/
	async getProfileChunk({ organizationSlug, profilerId, projectId, start, end }, opts) {
		const queryParams = new URLSearchParams();
		queryParams.set("profiler_id", profilerId);
		queryParams.set("project", projectId.toString());
		queryParams.set("start", start);
		queryParams.set("end", end);
		const path = apiPath`/organizations/${organizationSlug}/profiling/chunks/` + `?${queryParams.toString()}`;
		const body = await this.requestJSON(path, void 0, opts);
		const response = ProfileChunkResponseSchema.parse(body);
		if (response.chunks.length === 0) throw new ApiNotFoundError(`No profile chunk found for profiler_id ${profilerId}`, void 0, body);
		return {
			...response.chunks[0],
			profiler_id: response.chunks[0].profiler_id ?? profilerId
		};
	}
	getPreprodSnapshotUrl(organizationSlug, snapshotId) {
		return getPreprodSnapshotUrl(this.host, organizationSlug, snapshotId, this.protocol);
	}
	async getSnapshotDetails({ organizationSlug, snapshotId, compactMetadata = true }) {
		const params = new URLSearchParams();
		if (compactMetadata) params.set("compact_metadata", "true");
		const path = apiPath`/organizations/${organizationSlug}/preprodartifacts/snapshots/${snapshotId}/` + `?${params.toString()}`;
		return this.requestJSON(path);
	}
	async getSnapshotImageDetail({ organizationSlug, snapshotId, imageIdentifier }) {
		const path = apiPath`/organizations/${organizationSlug}/preprodartifacts/snapshots/${snapshotId}/images/${imageIdentifier}/`;
		return this.requestJSON(path);
	}
	async fetchImageByUrl(imageUrl) {
		const response = imageUrl.startsWith("https://") ? await fetch(imageUrl) : await this.request(imageUrl.startsWith("/api/0") ? imageUrl.slice(6) : imageUrl);
		if (!response.ok) throw new Error(`Failed to fetch image: ${response.status} ${response.statusText}`);
		const blob = await response.blob();
		let contentType = response.headers.get("content-type") ?? "";
		if (!contentType.startsWith("image/")) {
			const { detectImageMimeType } = await import("./internal/blob-utils.js");
			contentType = await detectImageMimeType(blob) ?? contentType;
		}
		return {
			blob,
			contentType
		};
	}
	async getLatestBaseSnapshot({ organizationSlug, appId, branch, project, projectSlug, compactMetadata = true }) {
		const params = new URLSearchParams();
		params.set("app_id", appId);
		if (branch) params.set("branch", branch);
		if (project) params.set("project", project);
		if (projectSlug) params.set("projectSlug", projectSlug);
		if (compactMetadata) params.set("compact_metadata", "true");
		const path = apiPath`/organizations/${organizationSlug}/preprodartifacts/snapshots/latest-base/` + `?${params.toString()}`;
		return this.requestJSON(path);
	}
};

//#endregion
export { SentryApiService };
//# sourceMappingURL=client-IUNQkit5.js.map